import { ShowsSlider, ShowsSliderProps } from '@/components/ShowsSlider/ShowsSlider';
import { ShowsSliderHeaderProps } from '@/components/ShowsSlider/ShowsSlider.Header';
import { ShowsSliderLinkProps } from '@/components/ShowsSlider/ShowsSlider.Link';
import { ShowsSliderSlideProps } from '@/components/ShowsSlider/ShowsSlider.Slide';
import { ShowsSliderTitleProps } from '@/components/ShowsSlider/ShowsSlider.Title';
import { StandaloneSlideProps } from '@/components/element/Slider/Slide';
import { StandaloneSwiperProps } from '@/components/element/Slider/Swiper';
import { ClassNameProvider } from '@/styles/ShowsSlider';
import { LabradorComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { useAppState } from 'lib/hooks/useAppState';
import { mergeOptions } from 'lib/utils/mergeProps';
import { standaloneComponent } from 'modules/dynamic-components';

const Swiper = standaloneComponent<StandaloneSwiperProps>('ElementSliderSwiper');
const Slide = standaloneComponent<StandaloneSlideProps>('ElementSliderSlide');

export interface Show {
  description?: string;
  src?: string;
  playlistCategory?: string;
  playlistId?: string;
  title?: string;
  url?: string;
}

export interface StandaloneShowsSliderProps extends StandaloneComponentProps {
  shows?: Show[];
  caption?: React.ReactNode;
  headline?: React.ReactNode;
  options?: ShowsSliderProps & {
    $slide?: ShowsSliderSlideProps;
    $header?: ShowsSliderHeaderProps;
    $title?: ShowsSliderTitleProps;
    $link?: ShowsSliderLinkProps['options'];
    $swiperSlide?: StandaloneSlideProps['options'];
    $swiper?: StandaloneSwiperProps['options'];
  };
}

export const StandaloneShowsSlider: StandaloneComponent<StandaloneShowsSliderProps> = ({
  headline,
  shows,
  caption,
  options,
  ...props
}) => {
  const { $swiper, $slide, $link, $swiperSlide, $header, $title, ...$base } = options ?? {};

  if (!shows?.length) return null;

  const slides = shows.map((show: Show) => (
    <Slide
      url={`${show.url}?pos=showslider`}
      key={`${show.playlistId}-${show.title}`}
      title={
        <ShowsSlider.Slide {...$slide}>
          <span>{show.title}</span>
        </ShowsSlider.Slide>
      }
      imageSrc={show.src}
      options={{
        $link: {
          className: '!ml-0  cts-impression-item',

          'data-cts-label': `shows|${show.playlistId}`,
          'data-cts-name': `shows|${show.playlistId}`,
          'data-cts-creative': `shows|${show.playlistId}`,
          'data-cts-id': `${show.playlistId}`,
        },
        $image: {
          className: 'aspect-vertical',
        },
        ...$swiperSlide,
      }}
    />
  ));

  return (
    <ClassNameProvider value={$base}>
      <div className="cts-impression-group">
        <ShowsSlider {...$base} {...props}>
          <ShowsSlider.Header {...$header}>
            <ShowsSlider.Title {...$title}>{headline || 'Våra program'}</ShowsSlider.Title>
            <ShowsSlider.Link
              href="/video/show"
              content={<ShowsSlider.Caption>{caption || 'Se alla'}</ShowsSlider.Caption>}
              data-cts-label="shows|more-programs"
              data-cts-name="shows|more-programs"
              data-cts-creative="shows|more-programs"
              options={mergeOptions({ className: 'cts-impression-item' }, $link)}
            />
          </ShowsSlider.Header>
          <Swiper
            content={slides}
            hideNavigation
            options={mergeOptions(
              {
                speed: 300,
                autoplay: false,
                loop: false,

                breakpoints: {
                  0: { slidesPerView: 2.5 },
                  360: { slidesPerView: 2.5 },
                  480: { slidesPerView: shows.length },
                },
                $arrow: {
                  className: 'md:hidden',
                },
              },
              $swiper
            )}
          />
        </ShowsSlider>
      </div>
    </ClassNameProvider>
  );
};

const DynamicStandaloneShowsSlider = standaloneComponent('ContentShowsSlider') as typeof standalone;

const LabradorShowsSlider: LabradorComponent = ({ meta, data, descendants }) => {
  const [{ type }] = useAppState();

  const maxSlideItems = type === 'front' ? 6 : 3;
  const showsItems = descendants.map((descendant) => descendant.data).slice(0, maxSlideItems);

  return <DynamicStandaloneShowsSlider shows={showsItems} />;
};

export const standalone = StandaloneShowsSlider;

export default LabradorShowsSlider;
